import { filter } from 'lodash';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
// material
import {
  Card,
  Table,
  Stack,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer
} from '@mui/material';
import { Box } from '@mui/system';
// components
import Page from '../../components/Page';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead, UserMoreMenu } from '../../components/_dashboard/user';
import BrudCrumbs from '../../components/BreadCrumbs';
import PageTitle from '../../components/PageHeading';
import ApiErrorMessage from '../Global/ApiErrorMessage';
import Loader from '../Global/Loader';
// utils & services
import { fDate } from '../../utils/formatTime';
import RevenueService from '../services/RevenueService';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getCustomerList } from '../../store/Slice/Customer/CustomerSlice';
import useDebounce from '../../utils/useDebounce';
import useCustomerSelector from '../../store/Slice/Customer/CustomerSelector';
import SimplePagination from '../../components/SimplePagination';
// styling
import { StyledTableRow, StyledTableCell } from '../Global/Styling';
import CustomerToolbar from './CustomerToolbar';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'id', label: 'Customer ID', alignRight: false },
  { id: 'name', label: 'Customer NAME', alignRight: false },
  { id: 'city', label: 'Location', alignRight: false },
  { id: 'created', label: 'Relationship Since', alignRight: false },
  { id: 'randomPhoneNumber', label: 'Mobile', alignRight: false },
  { id: 'status', label: 'Loan status', alignRight: false },
  { id: 'loanType', label: 'Loan Type', alignRight: false },
  { id: '', label: 'ACTION' }
];

const LABEL_COLOR_MAP = {
  New: 'info',
  Contacted: 'info',
  DUPLICATE: 'warning',
  NEGATIVE: 'error',
  InActive: 'error',
  DND: 'warning',
  Completed: 'success',
  Active: 'success',
  Submitted: 'success'
};

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_user) => (_user.customerName || '').toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function CustomerList() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const rowsPerPage = 10;
  const [pageMenu] = useState([]);
  const [openLoader] = useState(false);
  const [openApiError, setOpenApiError] = useState(false);
  const [errorMessage] = useState('');
  const [hasNextPage, setHasNextPage] = useState(true);
  const [filterState, setFilterState] = useState({
    typeId: null,
    isActive: null,
    cityId: null
  });

  const { LoginData } = useLoginSelector();
  const dispatch = useDispatch();
  const debouncedSearch = useDebounce(filterName, 1000);
  const { CustomerListData } = useCustomerSelector();
  const { DownloadCustomerRecord } = RevenueService;

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = (CustomerListData || []).map((n) => n.customerId);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const customerList = useMemo(
    () => (Array.isArray(CustomerListData) ? CustomerListData : []),
    [CustomerListData]
  );

  const filteredUsers = useMemo(
    () => applySortFilter(customerList, getComparator(order, orderBy), filterName),
    [customerList, order, orderBy, filterName]
  );

  const isUserNotFound = filteredUsers.length === 0;

  const handleClick = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((item) => item !== id);
    }

    setSelected(newSelected);
  };

  useEffect(() => {
    setHasNextPage((CustomerListData?.length || 0) >= rowsPerPage);
  }, [CustomerListData, rowsPerPage]);

  const getCustomerListApi = useCallback(() => {
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      filterModel: {
        typeId: filterState.typeId || null,
        isActive: filterState.isActive || null,
        cityId: filterState.cityId || null
      },
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 1 ? debouncedSearch : null,
        pageSize: rowsPerPage,
        pageNumber: page + 1,
        sortOrder: order
      }
    };
    dispatch(getCustomerList(credentials));
  }, [LoginData, filterState, rowsPerPage, page, order, debouncedSearch, dispatch]);

  useEffect(() => {
    getCustomerListApi();
  }, [getCustomerListApi]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const getLabelColor = useCallback((s) => LABEL_COLOR_MAP[s] || 'warning', []);

  const handleDownload = async () => {
    const credentials = {
      userId: LoginData?.userId,
      customerIds: selected,
      listModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        pageSize: rowsPerPage,
        pageNumber: page + 1,
        sortOrder: order
      },
      filters: {
        typeId: null,
        cityId: null,
        isActive: true
      }
    };

    try {
      const response = await DownloadCustomerRecord(credentials, LoginData?.jwt);
      const contentType = response.headers.get('content-type');
      const blob = await response.blob();

      const fileName = `CustomerDataDownload_${format(new Date(), 'dd-MM-yyyy_HH-mm-ss')}.xlsx`;
      const mimeType =
        contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  return (
    <Page title="Kpros by Kalolytic Solutions || Customer">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="List of Customer" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Card>
            <CustomerToolbar
              numSelected={selected.length}
              filterName={filterName}
              onFilterName={handleFilterByName}
              parent="Customer"
              pageMenu={pageMenu}
              filterState={filterState}
              setFilterState={setFilterState}
              setFilterName={setFilterName}
              handleDownload={handleDownload}
            />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 800, height: '50vh', overflow: 'auto' }}>
                <Table>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={customerList.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                    isCheckBox="true"
                  />
                  <TableBody>
                    {filteredUsers.map((row) => {
                      const {
                        cityName,
                        customerId,
                        customerName,
                        mobile,
                        relationshipSince,
                        statusLabel,
                        loanType
                      } = row;
                      const isItemSelected = selected.indexOf(customerId) !== -1;

                      return (
                        <StyledTableRow
                          hover
                          key={customerId}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isItemSelected}
                              onChange={() => handleClick(customerId)}
                            />
                          </TableCell>

                          <StyledTableCell align="left">{customerId}</StyledTableCell>
                          <StyledTableCell align="left">{customerName}</StyledTableCell>
                          <StyledTableCell align="left">{cityName}</StyledTableCell>
                          <StyledTableCell align="left">{fDate(relationshipSince)}</StyledTableCell>
                          <StyledTableCell align="left">{mobile}</StyledTableCell>
                          <StyledTableCell align="left">
                            <Label variant="ghost" color={getLabelColor(statusLabel)}>
                              {statusLabel}
                            </Label>
                          </StyledTableCell>

                          <StyledTableCell align="left">{loanType}</StyledTableCell>
                          <StyledTableCell align="" width={80}>
                            <UserMoreMenu
                              rowId={customerId}
                              parent="customer"
                              pageMenu={pageMenu}
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                  {isUserNotFound && (
                    <TableBody>
                      <TableRow>
                        <StyledTableCell
                          align="center"
                          colSpan={TABLE_HEAD.length + 1}
                          sx={{ py: 3 }}
                        >
                          <SearchNotFound searchQuery={filterName} />
                        </StyledTableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>

            <SimplePagination page={page} setPage={setPage} hasNextPage={hasNextPage} />
          </Card>
          <Loader openLoader={openLoader} />

          <ApiErrorMessage
            openApiError={openApiError}
            setOpenApiError={setOpenApiError}
            errorMessage={errorMessage}
          />
        </Box>
      </Container>
    </Page>
  );
}
